'use client'

import React, { FunctionComponent, useEffect, useState, useCallback } from 'react'
import { usePathname } from 'next/navigation'
import dynamic from 'next/dynamic'
import classnames from 'classnames'
import {
  useResponsive,
  useSiteContext,
  useTranslations,
} from '@dustin-web/microsite-components/src/hooks'
import ErrorBoundary from '@dustin-web/microsite-components/src/utils/error-boundary'
import CookieBanner from '@dustin-web/microsite-components/src/cookie-banner'
import { DevUtils } from '@dustin-web/microsite-components/src/dev/dev-admin'
import ScrollToTopButton from '@dustin-web/microsite-baselayout/src/components/scroll-to-top-btn'
import { useAuth } from '@dustin-web/microsite-components/src/auth'
import { useCookieState } from '@dustin-web/microsite-components/src/hooks/use-cookie-state'
import { constants } from '@dustin-web/microsite-config'
import EolIe from '@dustin-web/microsite-components/src/eol-ie'
import BreakpointIndicator from '@dustin-web/microsite-baselayout/src/components/breakpoint-indicator'
import CartNotification from '@dustin-web/microsite-baselayout/src/components/header/cart/cart-notification'
import { RenderScripts } from '@dustin-web/microsite-baselayout/src/components/3rd-party-scripts'
import ThemeColor from '@dustin-web/microsite-baselayout/src/components/theme-color'
import { useFeatureFlag } from '@dustin-web/microsite-data/src/feature-flags/client'

const MicrositeContextDevAdmin = dynamic(
  () => import('@dustin-web/microsite-components/src/dev/dev-admin')
)
const BlackFridaySignup = dynamic(
  () => import('@dustin-web/microsite-components/src/black-friday-signup')
)

type Props = {
  disableSiteSelector: boolean
  devUtils: DevUtils
  children: React.ReactNode
}

const AppBaseLayoutEffects: FunctionComponent<Props> = ({ devUtils, children }) => {
  const { isSmallScreen } = useResponsive()
  const pathname = usePathname() || ''
  const isStartPage = pathname.split('?')[0] === '/' ? true : false
  const isShoppingCart = pathname.split('?')[0] === '/shoppingcart' ? true : false
  const { userData } = useAuth()

  const { enabled: isBlackFridaySignupEnabled } = useFeatureFlag('black-friday-signup')
  const { enabled: isBreakpointIndicatorEnabled } = useFeatureFlag('breakpoint-indicator', true)
  const { enabled: isDevAdminEnabled } = useFeatureFlag('dev-admin-ui', true)

  const { translate } = useTranslations()
  const { cookies, setCookie } = useCookieState()
  const isAdmin = userData?.allowImpersonateContact ?? false
  const [isIE11, setIsIE11] = useState<boolean>(false)

  const changeUsp = useCallback(() => {
    const hideUspPaths = ['oplossingen']
    const path = pathname.split('/')[1]
    const hideUsp = hideUspPaths.some(hideUspPath => hideUspPath === path)
    const usp = document.getElementById('usp-bar')
    // Hide usp-bar on certain paths
    if (hideUsp) {
      usp?.classList.add('d-none')
    }
    // Remove border-bottom from usp if startpage has a takeover-banner
    else if (isStartPage) {
      const hasTakover = document.getElementsByClassName('c-startpage-takeover-wrapper')[0]
      if (hasTakover) {
        usp?.getElementsByClassName('c-box--section')[0].classList.remove('u-bb')
      }
    }
  }, [pathname, isStartPage])

  useEffect(() => {
    changeUsp()
    if ('ActiveXObject' in window) {
      alert(isIE11)

      setTimeout(function () {
        setIsIE11(true)
      }, 1)
    }
  }, [pathname, changeUsp, isIE11])

  useEffect(() => {
    if (isAdmin && !cookies.userSettings.showMargins) {
      setTimeout(() => {
        const result = confirm(translate('Show_Margins'))
        setCookie(constants.cookies.userSettings.showMargins, result)
      }, 500)
    }
  }, [isAdmin, cookies.userSettings.showMargins, translate, setCookie])

  const { siteUrl, isProxied, isDevelopment } = useSiteContext()
  const isStandaloneProxied = siteUrl.includes('dustin.services') && isProxied

  const shouldShowDevAdmin = isDevelopment || !isProxied || isStandaloneProxied || isDevAdminEnabled

  const [showDevAdmin, setShowDevAdmin] = useState(false)

  useEffect(() => {
    if (shouldShowDevAdmin) {
      setShowDevAdmin(true)
    }
  }, [shouldShowDevAdmin])

  return (
    <>
      <div
        className={classnames('business', {
          startpage: isStartPage,
        })}>
        {isIE11 === true ? (
          <div
            className="t-light-blue d-flex justify-content-center u-pb-79"
            style={{ height: '100vh', paddingTop: '80px' }}>
            <EolIe />
          </div>
        ) : (
          <>
            {children}
            <ErrorBoundary>
              <RenderScripts />
            </ErrorBoundary>
            <ErrorBoundary>
              <CookieBanner />
            </ErrorBoundary>
            <ErrorBoundary>
              {!isShoppingCart && !isSmallScreen && <CartNotification />}
            </ErrorBoundary>
            <ErrorBoundary>
              <ScrollToTopButton />
            </ErrorBoundary>
            <ErrorBoundary>
              <ThemeColor />
            </ErrorBoundary>
          </>
        )}
        <ErrorBoundary>{isBreakpointIndicatorEnabled && <BreakpointIndicator />}</ErrorBoundary>
        <ErrorBoundary>
          {showDevAdmin && <MicrositeContextDevAdmin devUtils={devUtils} />}
        </ErrorBoundary>
        {isBlackFridaySignupEnabled && <BlackFridaySignup />}
      </div>
    </>
  )
}

export default AppBaseLayoutEffects
